import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchVisits } from 'store/visits/actions';
import { VisitUiType } from 'store/visits/types';
import { useTimeZone } from 'shared/hooks/use-timezone';
import { useLocale, getDateFnsLocale, getWeekStartsOn } from 'shared/hooks/use-locale';
import { getAllFilters, getInvalidationToken, getPaginatedVisitsState } from 'store/visits/selectors';
import {
  getTimeFrameDateRange,
  convertToUTCISO,
} from 'components/v2/date-picker-with-presets/date-picker-with-presets.utils';

interface UseVisitsDateFilterParams {
  ownerId: string | undefined;
}

interface UseVisitsDateFilterReturn {
  startDate: string | null;
  endDate: string | null;
  selectedTimeFrame: string | null;
  handleStartDateChange: (date: string | null) => void;
  handleEndDateChange: (date: string | null) => void;
  handleTimeFrameChange: (timeFrame: string | null) => void;
  after?: string;
  before?: string;
}

export const useVisitsDateFilter = ({ ownerId }: UseVisitsDateFilterParams): UseVisitsDateFilterReturn => {
  const dispatch = useDispatch();
  const timezone = useTimeZone();
  const locale = useLocale();
  // Which day the reader's locale starts the week on, so the "this week" / "last week" presets ask
  // the API for the same seven days the calendar draws.
  const weekStartsOn = useMemo(() => getWeekStartsOn(getDateFnsLocale(locale)), [locale]);
  const filters = useSelector(getAllFilters);
  const invalidationToken = useSelector(getInvalidationToken);
  const { total } = useSelector(getPaginatedVisitsState(VisitUiType.ALL));

  // Initialize with "today" as default
  const defaultDateRange = useMemo(() => {
    return getTimeFrameDateRange('today', timezone, weekStartsOn);
  }, [timezone, weekStartsOn]);

  const [startDate, setStartDate] = useState<string | null>(defaultDateRange.start);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [selectedTimeFrame, setSelectedTimeFrame] = useState<string | null>('today');

  // Convert date values to UTC ISO strings for API
  const getDateParams = useCallback((): { after?: string; before?: string } => {
    const params: { after?: string; before?: string } = {};

    if (startDate) {
      // For start date, use start of day
      params.after = convertToUTCISO(startDate, timezone, false);

      // If no end date, use start date as end date (for single-day filtering)
      const endDateString = endDate || startDate;
      // For end date, use end of day
      params.before = convertToUTCISO(endDateString, timezone, true);
    }

    return params;
  }, [startDate, endDate, timezone]);

  // Fetch visits when dates or ownerId changes
  useEffect(() => {
    if (ownerId) {
      const dateParams = getDateParams();
      dispatch(
        fetchVisits({
          type: VisitUiType.ALL,
          buildingId: ownerId,
          limit: 50,
          offset: 0,
          ...dateParams,
        }),
      );
    }
  }, [dispatch, ownerId, filters, getDateParams]);

  // The EMPTY state is the one refresh the infinite-scroll hook can't cover: when the list has no visits,
  // `VisitsViewPageV2` unmounts it, so re-read the first page here on an SSE change. `allVisitsTotal` is
  // reset to `null` (not `0`) after cancelling the last visit, so treat `null` as empty too — that is
  // exactly the window this net is for. Background, so the empty state doesn't flash a spinner over itself.
  const prevInvalidationToken = useRef(invalidationToken);
  useEffect(() => {
    if (prevInvalidationToken.current === invalidationToken) {
      return;
    }
    prevInvalidationToken.current = invalidationToken;
    if (!ownerId || (total ?? 0) > 0) {
      return;
    }
    dispatch(
      fetchVisits({
        type: VisitUiType.ALL,
        buildingId: ownerId,
        limit: 50,
        offset: 0,
        background: true,
        ...getDateParams(),
      }),
    );
  }, [invalidationToken, ownerId, total, getDateParams, dispatch]);

  // Advance a relative preset (today / this week / …) across the building's midnight, on a CLOCK timer
  // rather than off the SSE token. A screen left open overnight would otherwise keep showing — and
  // querying — yesterday's window, since `startDate` is a one-time initial value. Driving this from the
  // wall clock (not an invalidation) fixes the header display as well as the query, and avoids racing the
  // list's own SSE refresh: at midnight the dates change, the main fetch effect re-reads the fresh window,
  // and any later frame already sees today's `after`/`before`. An explicitly-picked absolute range (no
  // `selectedTimeFrame`) is never auto-advanced. Functional setState + a recompute-on-fire so this depends
  // only on the preset/timezone, not on the dates it sets.
  useEffect(() => {
    if (!selectedTimeFrame) {
      return undefined;
    }
    let timer: ReturnType<typeof setTimeout>;
    const scheduleNextMidnight = (): void => {
      // End-of-today in the building tz, as UTC; the next building-midnight is one millisecond past it.
      const endOfTodayUTC = getTimeFrameDateRange('today', timezone, weekStartsOn).endUTC;
      const msUntilMidnight = Math.max(0, new Date(endOfTodayUTC).getTime() + 1 - Date.now());
      timer = setTimeout(() => {
        const range = getTimeFrameDateRange(selectedTimeFrame, timezone, weekStartsOn);
        const isSingleDatePreset = selectedTimeFrame === 'today' || selectedTimeFrame === 'yesterday';
        const nextEnd = isSingleDatePreset ? null : range.end;
        setStartDate((prev) => (prev !== range.start ? range.start : prev));
        setEndDate((prev) => (prev !== nextEnd ? nextEnd : prev));
        scheduleNextMidnight();
      }, msUntilMidnight);
    };
    scheduleNextMidnight();
    return () => clearTimeout(timer);
  }, [selectedTimeFrame, timezone, weekStartsOn]);

  const handleStartDateChange = useCallback((date: string | null): void => {
    setStartDate(date);
  }, []);

  const handleEndDateChange = useCallback((date: string | null): void => {
    setEndDate(date);
  }, []);

  const handleTimeFrameChange = useCallback(
    (timeFrame: string | null): void => {
      setSelectedTimeFrame(timeFrame);

      if (!timeFrame) {
        return;
      }

      const range = getTimeFrameDateRange(timeFrame, timezone, weekStartsOn);
      setStartDate(range.start);

      // For single date presets, clear end date
      const isSingleDatePreset = timeFrame === 'today' || timeFrame === 'yesterday';
      setEndDate(isSingleDatePreset ? null : range.end);
    },
    [timezone, weekStartsOn],
  );

  const dateParams = getDateParams();

  return {
    startDate,
    endDate,
    selectedTimeFrame,
    handleStartDateChange,
    handleEndDateChange,
    handleTimeFrameChange,
    after: dateParams.after,
    before: dateParams.before,
  };
};
